import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";

// the view the user is looking at, kept in the url so a reload, a shared link
// or a remount of the table lands on the same statement, period and year. a
// hand-edited value falls back instead of failing the route
export const financialExplorerSearchSchema = z.object({
  statement: z.enum(["balance", "profit-loss"]).optional().catch(undefined),
  view: z.enum(["years", "quarters", "months"]).default("years").catch("years"),
  // years compared side by side; quarters 1-4 or months 1-12 picked from them
  years: z
    .array(z.coerce.number().int().positive())
    .optional()
    .catch(undefined),
  periods: z
    .array(z.coerce.number().int().min(1).max(12))
    .optional()
    .catch(undefined),
  cumulative: z.boolean().optional().catch(undefined),
});

export type FinancialExplorerSearch = z.infer<
  typeof financialExplorerSearchSchema
>;

export function useFinancialExplorerSearch() {
  const raw = useSearch({ strict: false });
  const search = useMemo(() => financialExplorerSearchSchema.parse(raw), [raw]);
  const navigate = useNavigate();

  const setSearch = useCallback(
    ({ view, cumulative, ...updates }: Partial<FinancialExplorerSearch>) => {
      void navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          ...updates,
          // the default view is what an empty url means, so it is not written
          ...(view && { view: view === "years" ? undefined : view }),
          ...(cumulative !== undefined && {
            cumulative: cumulative || undefined,
          }),
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return [search, setSearch] as const;
}
